import { useQueryCart } from '@/features/shop/services/Cart/queries'
import { type Product } from '@/features/shop/services/Product/types'
import {
  useFeaturePromoToast,
  useFetchPromosAndShowToast,
} from '@/features/shop/services/Campaigns/hooks'
import { useRefreshCouponsAndShowCouponToast } from '@/features/shop/services/Coupons/hooks'
import { type Cart } from '@/features/shop/services/Cart/types'
import { type MutationProduct } from '@shared/types'
import { getOrderDiscountPromotionValues } from '@/features/shop/services/Campaigns/utils'
import { type AvailablePromotionsPayload } from '@/features/shop/services/Campaigns/types'
import { useShowToast } from '@/features/shared/state/Toast/hooks'
import { ToastType } from '@/features/shared/constants/toastTypes'
import { useGetIsEligibleForC360NoMarkup } from '@/features/entrypoint/components/Circle360/useIsEligibleForC360NoMarkup'
import {
  getIsC360ElementExpired,
  setSeenC360Element,
} from '@/features/entrypoint/components/Circle360/utils'
import { useRetailerContext } from '@/features/shared/context/RetailerContext'

export const useIsCartRestricted = () => {
  const cart = useQueryCart().data
  return Boolean(cart?.is_alcohol || cart?.age_restriction)
}

export const useCartShoppingCartLines = (): Product[] => {
  return useQueryCart().data?.shopping_cart_lines ?? []
}

export const useCartOnSuccess = () => {
  const showFeaturePromoToast = useFeaturePromoToast()
  const showOrderPromoToast = useFetchPromosAndShowToast()
  const refreshCouponsAndShowCouponToast = useRefreshCouponsAndShowCouponToast()
  const { showToast } = useShowToast()
  const getIsEligibleForC360NoMarkup = useGetIsEligibleForC360NoMarkup()
  const isGlobalContext = useRetailerContext()?.isGlobalContext ?? false

  return async (
    currentCart: { promotions: AvailablePromotionsPayload },
    product: MutationProduct,
    previousCart: Cart | undefined,
  ) => {
    const { couponId, cpgPromotion } = product
    const { promotions: prevPromotions } = previousCart ?? {}
    const { isNextOrderDiscount, progress } = getOrderDiscountPromotionValues(
      prevPromotions?.availablePromotions,
    )
    const currentPromotions = currentCart.promotions
    const prevQty =
      previousCart?.shopping_cart_lines.find((line) =>
        product.isCustomProduct ? line.name === product.name : line.id === product.id,
      )?.quantity ?? 0

    const showNoMarkupsToast = () => {
      setSeenC360Element('toast', product.storeId)
      showToast(ToastType.NO_MARKUPS_TOAST, {
        ariaLabel: "You're paying $0 in markups",
        props: {
          title: "You're paying $0 in markups.",
        },
        optionalStyling: {
          boxShadow: `0 0 4px 0 #0000001a, 0 6px 6px -4px #00000033`,
        },
      })
    }

    const showC360Toast =
      getIsEligibleForC360NoMarkup() &&
      getIsC360ElementExpired('toast', product.storeId) &&
      !isGlobalContext &&
      product.nmu_eligible &&
      product.quantity > prevQty

    if (couponId) {
      const didShowToast = await refreshCouponsAndShowCouponToast(couponId)
      if (!didShowToast && showC360Toast) {
        showNoMarkupsToast()
      }
      return
    }
    if (isNextOrderDiscount && progress >= 0.7) {
      const didShowToast = showOrderPromoToast(progress, currentPromotions)
      if (!didShowToast && showC360Toast) {
        showNoMarkupsToast()
      }
    } else {
      const didShowToast = showFeaturePromoToast(
        currentPromotions,
        prevPromotions,
        cpgPromotion?.category_id,
      )
      if (!didShowToast && showC360Toast) {
        showNoMarkupsToast()
      }
    }
  }
}
